import {
  Button,
  Modal,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Select,
  FormControl,
  MenuItem,
} from "@mui/material"
import MDBox from "../MDBox"
import MDTypography from "../MDTypography"
import moment from "moment/moment"
import { DatePicker } from "@mui/x-date-pickers"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { useEffect, useMemo, useRef, useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import ChevronDownIcon from "assets/images/apollo-english/chevron-down.svg"
import { ReactComponent as FolderIcon } from "components/icons/svgs/folder.svg"
import ModalLeaveInfo from "components/ModalLeaveInfo/ModalLeaveInfo"
import AttachFileUpload from "components/AttachFileUpload/AttachFileUpload"

import { useLazyGetNotiApprovalLeaveQuery } from "shared/redux/endpoints/lxo"
import { useLazyGetLeaveTypesQuery } from "shared/redux/endpoints/leaveRequest"

const DATE_FORMAT = "DD/MM/YYYY"

// Sample user data with avatars
const sampleUsers = [
  { id: 1, name: "Theresa Webb", avatar: "https://i.pravatar.cc/150?img=1" },
  {
    id: 2,
    name: "Cameron Williamson",
    avatar: "https://i.pravatar.cc/150?img=2",
  },
  {
    id: 3,
    name: "Darlene Robertson",
    avatar: "https://i.pravatar.cc/150?img=3",
  },
  { id: 4, name: "Savannah Nguyen", avatar: "https://i.pravatar.cc/150?img=4" },
  { id: 5, name: "Wade Warren", avatar: "https://i.pravatar.cc/150?img=5" },
  { id: 6, name: "Devon Lane", avatar: "https://i.pravatar.cc/150?img=6" },
  { id: 7, name: "Kristin Watson", avatar: "https://i.pravatar.cc/150?img=7" },
  {
    id: 8,
    name: "Brooklyn Simmons",
    avatar: "https://i.pravatar.cc/150?img=8",
  },
]


/**
 * Reusable UserDropdown component (for Approver & Notify)
 */
const UserDropdown = ({
  open,
  inputValue,
  onSelect,
  excludeIds = [],
  users,
}) => {
  const filteredUsers = useMemo(() => {
    const keyword = inputValue.trim().toLowerCase()

    const base = keyword
      ? users.filter((u) => u.name.toLowerCase().includes(keyword))
      : users

    return base.filter((u) => !excludeIds.includes(u.id))
  }, [inputValue, excludeIds, users])

  if (!open || filteredUsers.length === 0) return null

  return (
    <MDBox
      sx={{
        position: "relative",
        zIndex: 10,
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        maxHeight: "200px",
        overflowY: "auto",
        boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        backgroundColor: "white",
      }}
    >
      {filteredUsers.map((user) => (
        <MDBox
          key={user.id}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            cursor: "pointer",
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
          // onMouseDown để không bị blur đóng dropdown trước khi click
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onSelect(user)}
        >
          <MDBox
            component="img"
            src={user.avatar}
            alt={user.name}
            sx={{ width: "24px", height: "24px", borderRadius: "50%" }}
          />
          <MDTypography variant="body2">{user.name}</MDTypography>
        </MDBox>
      ))}
    </MDBox>
  )
}

/**
 * Main Component
 */
const ModalLeaveApplication = ({
  isOpen,
  onSubmit = () => {},
  onClose = () => {},
  dataEdit,
}) => {
  const [state, setState] = useState({
    fromDate: moment(),
    toDate: moment().add(1, "days"),
    isHalfDay: false,
    leaveType: null,
    approver: null,
    approverInput: "",
    approverOpen: false,
    notiOpen: false,
    notifyTo: [],
    notifyInput: "",
    reason: "",
    selectedFiles: [], // ✅ support multiple files
    attachments: [],
  })

  const uploadRef = useRef(null)

  const [error, setError] = useState({})
  const [isOpenModalLeaveInfo, setIsOpenModalLeaveInfo] = useState(false)
  const [triggerGetNotiApprovalLeave] = useLazyGetNotiApprovalLeaveQuery()
  const [triggerGetLeaveTypes] = useLazyGetLeaveTypesQuery()
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([])
  const [optionsTarget, setOptionsTarget] = useState([])
  const [optionsNotify, setOptionsNotify] = useState([])

  useEffect(() => {
    triggerGetLeaveTypes().then((res) => {
      const data = res?.data?.data ?? res?.data ?? []
      if (Array.isArray(data)) {
        setLeaveTypeOptions(
          data.map((item) => ({ value: item.code, label: item.nameEn }))
        )
      }
    })
  }, [])

  /**
   * Calculate total leave
   */
  const totalLeave = useMemo(() => {
    if (
      !state.fromDate ||
      !state.toDate ||
      !state.fromDate.isValid() ||
      !state.toDate.isValid()
    ) {
      return 0
    }

    if (state.toDate.isBefore(state.fromDate)) return 0

    const start = state.fromDate
    const end = state.toDate
    let days = end.diff(start, "days") + 1

    if (state.isHalfDay) days -= 0.5

    return days
  }, [state.fromDate, state.toDate, state.isHalfDay])

  /**
   * File change handler (max 5 files)
   */

  useEffect(() => {
    if (dataEdit) {
      setState({
        ...state,
        fromDate: moment(dataEdit.startDate),
        toDate: moment(dataEdit.endDate),
        isHalfDay: dataEdit.dayType === "FULL_DAY" ? false : true,
        leaveType: leaveTypeOptions.find(
          (item) => item.value === dataEdit.leaveType
        ),
        approver: dataEdit.approverId
          ? {
              id: dataEdit.approverId,
              name: dataEdit.approverFullName,
              avatar: dataEdit.approverAvatar,
            }
          : null,
        approverInput: dataEdit.approverId ? dataEdit.approverFullName : "",
        notifyTo: dataEdit?.notificationRecipientIds || [],
        notifyInput: "",
        reason: dataEdit.reason,
        selectedFiles: dataEdit.attachments,
        attachments: dataEdit.attachments || [],
      })
    }
  }, [dataEdit, leaveTypeOptions])

  useEffect(() => {
    triggerGetNotiApprovalLeave().then((res) => {
      if (res?.data) {
        const targetOptions = res.data.data.notificationTargets.map((item) => {
          return { ...item, name: item.fullName, id: item.userId }
        })

        const approverOptions = res.data.data.approvers.map((item) => {
          return { ...item, name: item.fullName, id: item.userId }
        })

        setOptionsTarget(targetOptions)
        setOptionsNotify(approverOptions)
      }
    })
  }, [])
  const handleFileChange = (event) => {
    const files = event.target.files ? Array.from(event.target.files) : []
    if (!files.length) return

    setState((prev) => {
      const combined = [...prev.selectedFiles, ...files]
      const maxFive = combined.slice(0, 5)
      return { ...prev, selectedFiles: maxFive }
    })
  }

  const handleRemoveFile = (fileIndex) => {
    setState((prev) => ({
      ...prev,
      selectedFiles: prev.selectedFiles.filter((_, idx) => idx !== fileIndex),
    }))
  }

  /**
   * Approver
   */
  const handleSelectApprover = (user) => {
    setState((prev) => ({
      ...prev,
      approver: user,
      approverInput: user.name,
    }))
  }

  const handleClearApprover = () => {
    setState((prev) => ({
      ...prev,
      approver: null,
      approverInput: "",
    }))
  }

  /**
   * Notify To
   */
  const handleAddNotifyUser = (user) => {
    setState((prev) => {
      if (prev.notifyTo.some((u) => u.id === user.id)) return prev
      return { ...prev, notifyTo: [...prev.notifyTo, user], notifyInput: "" }
    })
  }

  const handleRemoveNotifyUser = (userId) => {
    setState((prev) => ({
      ...prev,
      notifyTo: prev.notifyTo.filter((u) => u.id !== userId),
    }))
  }

  /**
   * Validation
   */
  const validate = () => {
    const requiresReason =
      state.leaveType?.value && state.leaveType.value !== "ANNUAL"
    const requiresAttachments =
      state.leaveType?.value && state.leaveType.value !== "ANNUAL"
    const currentAttachments =
      uploadRef.current?.getValue?.() ||
      state.attachments ||
      state.selectedFiles ||
      []

    const newError = {
      fromDate: !state.fromDate?.isValid() ? "From date is required" : "",
      toDate: !state.toDate?.isValid() ? "To date is required" : "",
      leaveType: !state.leaveType ? "Leave type is required" : "",
      approver: !state.approver?.id ? "Approver is required" : "",
      notifyTo: state.notifyTo.length === 0 ? "Notify to is required" : "",
      reason:
        requiresReason && !state.reason.trim() ? "Reason is required" : "",
      attachments:
        requiresAttachments && currentAttachments.length === 0
          ? "Attach file is required"
          : "",
    }

    Object.keys(newError).forEach((k) => {
      if (!newError[k]) delete newError[k]
    })

    setError(newError)
    return Object.keys(newError).length === 0
  }

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <div className="modal-leave-application-container">
          <div className="modal-leave-application-container-wrapper">
            {/* Header */}
            <MDBox className="modal-leave-application-header">
              <MDBox>
                <MDTypography
                  className="modal-leave-application-title"
                  variant="h5"
                >
                  Leave App
                </MDTypography>
              </MDBox>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </MDBox>

            {/* Content */}
            <MDBox className="modal-leave-application-content">
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <MDBox className="modal-leave-application-form">
                  {/* Dates */}
                  <MDBox className="modal-leave-application-form-dates">
                    <MDBox sx={{ flex: 1 }}>
                      <MDTypography
                        variant="subtitle2"
                        className="modal-leave-application-form-dates-pick"
                      >
                        From <sup>*{error.fromDate && <>Required</>}</sup>
                      </MDTypography>

                      <DatePicker
                        className="modal-leave-application-form-dates-select"
                        value={state.fromDate}
                        onChange={(newValue) => {
                          setState((prev) => ({
                            ...prev,
                            fromDate: moment(newValue),
                          }))
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            className="modal-leave-application-form-dates-select"
                          />
                        )}
                        disablePast
                        format={DATE_FORMAT}
                        sx={{
                          "& .MuiInputBase-root": {
                            color: "#9A9C9C !important",
                          },
                        }}
                      />
                    </MDBox>

                    <MDBox sx={{ flex: 1 }}>
                      <MDTypography
                        variant="subtitle2"
                        className="modal-leave-application-form-dates-pick"
                      >
                        To <sup>*{error.toDate && <>Required</>}</sup>
                      </MDTypography>

                      <DatePicker
                        className="modal-leave-application-form-dates-select"
                        value={state.toDate}
                        onChange={(newValue) => {
                          setState((prev) => ({
                            ...prev,
                            toDate: moment(newValue),
                          }))
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            className="modal-leave-application-form-dates-select"
                          />
                        )}
                        disablePast
                        minDate={state.fromDate}
                        format={DATE_FORMAT}
                      />
                    </MDBox>

                    <MDBox sx={{ display: "flex", alignItems: "flex-end" }}>
                      <FormControlLabel
                        className="modal-leave-application-form-dates-checkbox"
                        control={
                          <Checkbox
                            checked={state.isHalfDay}
                            onChange={(e) => {
                              setState((prev) => ({
                                ...prev,
                                isHalfDay: e.target.checked,
                              }))
                            }}
                          />
                        }
                        label="Half-day Leave"
                      />
                    </MDBox>
                  </MDBox>
                  {/* Leave type */}
                  <MDBox className="modal-leave-application-form-reason">
                    <MDTypography
                      variant="subtitle2"
                      className="modal-leave-application-form-dates-pick"
                    >
                      Leave type <sup>*{error.leaveType && <>Required</>}</sup>
                    </MDTypography>

                    <MDBox
                      item
                      sx={({ breakpoints }) => ({
                        [breakpoints.down("md")]: [{ width: "100%" }],
                      })}
                    >
                      <FormControl
                        sx={{ minWidth: 160, width: "100%" }}
                        size="medium"
                      >
                        <Select
                          id="leave-type-select-small"
                          value={state.leaveType?.value || ""} // ✅ fix warning
                          disabled={leaveTypeOptions.length === 0}
                          onChange={(e) => {
                            const selectedOption = leaveTypeOptions.find(
                              (option) => option.value === e.target.value
                            )
                            setState((prev) => ({
                              ...prev,
                              leaveType: selectedOption || null,
                            }))
                          }}
                          inputProps={{ "aria-label": "Without label" }}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <span style={{ color: "rgba(0, 0, 0, 0.38)" }}>
                                  Choose leave type
                                </span>
                              )
                            }
                            const selectedOption = leaveTypeOptions.find(
                              (option) => option.value === selected
                            )
                            return selectedOption
                              ? selectedOption.label
                              : selected
                          }}
                          IconComponent={() => (
                            <div
                              style={{
                                height: 30,
                                display: "flex",
                                alignItems: "center",
                                marginRight: 8,
                              }}
                            >
                              <img
                                src={ChevronDownIcon}
                                style={{ height: 12 }}
                              />
                            </div>
                          )}
                        >
                          {leaveTypeOptions.map((item, index) => (
                            <MenuItem key={index} value={item.value}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </MDBox>
                  </MDBox>
                  {/* Leave info + total */}
                  <MDBox className="modal-leave-application-form-leave-info-total">
                    <MDBox>
                      <Button
                        variant="contained"
                        color="info"
                        size="small"
                        className="btn-modal-leave-info"
                        loading={isOpenModalLeaveInfo}
                        onClick={() => setIsOpenModalLeaveInfo(true)}
                      >
                        Leave info
                      </Button>
                    </MDBox>

                    <MDBox>
                      <MDTypography variant="body2">
                        Total leave:&nbsp;&nbsp;
                        <b>
                          {totalLeave > 1
                            ? `${totalLeave} days`
                            : `${totalLeave} day`}
                        </b>
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  {/* Approver */}
                  <MDBox>
                    <MDTypography
                      variant="subtitle2"
                      className="modal-leave-application-form-dates-pick"
                    >
                      Approver <sup>*{error.approver && <>Required</>}</sup>
                    </MDTypography>

                    {state.approver ? (
                      <MDBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                          padding: "8px 12px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <MDBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <MDBox
                            component="img"
                            src={state.approver.avatar}
                            alt={state.approver.name}
                            sx={{
                              width: "24px",
                              height: "24px",
                              borderRadius: "50%",
                            }}
                          />
                          <MDTypography variant="body2">
                            {state.approver.name}
                          </MDTypography>
                        </MDBox>

                        <IconButton size="small" onClick={handleClearApprover}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </MDBox>
                    ) : (
                      <>
                        <TextField
                          fullWidth
                          placeholder="Select approver"
                          value={state.approverInput}
                          onChange={(e) => {
                            setState((prev) => ({
                              ...prev,
                              approverInput: e.target.value,
                            }))
                          }}
                          onFocus={() =>
                            setState((prev) => ({
                              ...prev,
                              approverOpen: true, // click/focus là mở
                            }))
                          }
                          onBlur={() => {
                            // để click được item trong dropdown thì delay 1 chút
                            setTimeout(() => {
                              setState((prev) => ({
                                ...prev,
                                approverOpen: false,
                              }))
                            }, 150)
                          }}
                          sx={{ mb: 1 }}
                          className="modal-leave-application-form-reason-box"
                        />

                        <UserDropdown
                          inputValue={state.approverInput}
                          onSelect={handleSelectApprover}
                          // users={optionsTarget}
                          users={optionsNotify}
                          open={state.approverOpen}
                        />
                      </>
                    )}
                  </MDBox>
                  {/* Notify to */}
                  <MDBox>
                    <MDTypography
                      variant="subtitle2"
                      className="modal-leave-application-form-dates-pick"
                    >
                      Notify To <sup>*{error.notifyTo && <>Required</>}</sup>
                    </MDTypography>

                    <MDBox
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        padding: state.notifyTo.length > 0 ? "8px" : "0",
                        backgroundColor:
                          state.notifyTo.length > 0 ? "#edf5ff" : "transparent",
                      }}
                    >
                      {state.notifyTo.length > 0 && (
                        <MDBox
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          {state.notifyTo.map((user) => (
                            <MDBox
                              key={user.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                background: "rgba(0, 137, 246, 0.10)",
                                borderRadius: "4px",
                                padding: "4px 8px",
                              }}
                            >
                              <MDBox
                                component="img"
                                src={user.avatar}
                                alt={user.name}
                                sx={{
                                  width: "20px",
                                  height: "20px",
                                  borderRadius: "50%",
                                }}
                              />
                              <MDTypography
                                variant="body2"
                                color="#0089F6"
                                sx={{ color: "#0089F6", fontSize: "12px" }}
                              >
                                {user.name}
                              </MDTypography>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveNotifyUser(user.id)}
                                sx={{ padding: "2px" }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width={7}
                                  height={7}
                                  viewBox="0 0 7 7"
                                  fill="none"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M4.32489 3.50007L6.82909 0.995875C7.05716 0.767797 7.05716 0.399138 6.82909 0.171059C6.60101 -0.0570197 6.23235 -0.0570197 6.00427 0.171059L3.50007 2.67526L0.995875 0.171059C0.767796 -0.0570197 0.399137 -0.0570197 0.171059 0.171059C-0.0570196 0.399138 -0.0570196 0.767797 0.171059 0.995875L2.67526 3.50007L0.171059 6.00427C-0.0570196 6.23235 -0.0570196 6.60101 0.171059 6.82909C0.284807 6.94283 0.434137 7 0.583467 7C0.732797 7 0.882127 6.94283 0.995875 6.82909L3.50007 4.32489L6.00427 6.82909C6.11802 6.94283 6.26735 7 6.41668 7C6.56601 7 6.71534 6.94283 6.82909 6.82909C7.05716 6.60101 7.05716 6.23235 6.82909 6.00427L4.32489 3.50007Z"
                                    fill="#343C44"
                                  />
                                </svg>
                              </IconButton>
                            </MDBox>
                          ))}
                        </MDBox>
                      )}

                      <TextField
                        fullWidth
                        placeholder="Add people to notify"
                        value={state.notifyInput}
                        onChange={(e) => {
                          setState((prev) => ({
                            ...prev,
                            notifyInput: e.target.value,
                          }))
                        }}
                        onFocus={() =>
                          setState((prev) => ({
                            ...prev,
                            notiOpen: true,
                          }))
                        }
                        onBlur={() => {
                          setTimeout(() => {
                            setState((prev) => ({
                              ...prev,
                              notiOpen: false,
                            }))
                          }, 150)
                        }}
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            border:
                              state.notifyTo.length > 0
                                ? "none"
                                : "1px solid #e0e0e0",
                          },
                          "& .MuiOutlinedInput-root": {
                            padding: 0,
                          },
                        }}
                        className="modal-leave-application-form-reason-box"
                      />
                    </MDBox>

                    <UserDropdown
                      inputValue={state.notifyInput}
                      onSelect={handleAddNotifyUser}
                      excludeIds={state.notifyTo.map((u) => u.id)}
                      // users={optionsNotify}
                      users={optionsTarget}
                      open={state.notiOpen}
                    />
                  </MDBox>
                  {/* Reason */}
                  <MDBox>
                    <MDTypography
                      variant="subtitle2"
                      className="modal-leave-application-form-dates-pick"
                    >
                      Reason{" "}
                      {state.leaveType?.value &&
                        state.leaveType.value !== "ANNUAL" && (
                          <sup>*{error.reason && <>Required</>}</sup>
                        )}
                    </MDTypography>

                    <TextField
                      className="modal-leave-application-form-reason-box"
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Please specify your reason for leave (optional for AL)"
                      value={state.reason}
                      onChange={(e) => {
                        setState((prev) => ({
                          ...prev,
                          reason: e.target.value,
                        }))
                      }}
                    />
                  </MDBox>
                  <AttachFileUpload
                    ref={uploadRef}
                    required={
                      state.leaveType?.value &&
                      state.leaveType.value !== "ANNUAL"
                    }
                    errorMessage={
                      state.leaveType?.value &&
                      state.leaveType.value !== "ANNUAL"
                        ? error.attachments
                        : ""
                    }
                    onChange={(files) => {
                      setState((prev) => ({
                        ...prev,
                        attachments: files,
                      }))
                    }}
                    selectedFiles={state.selectedFiles}
                  />
                </MDBox>
              </LocalizationProvider>
            </MDBox>

            {/* Footer */}
            <MDBox className="modal-leave-application-footer">
              <Button
                variant="outlined"
                onClick={onClose}
                className="cancel-button"
              >
                Cancel
              </Button>

              <Button
                className="submit-button"
                variant="contained"
                color="primary"
                onClick={() => {
                  if (!validate()) return
                  onSubmit(state)
                }}
              >
                Submit
              </Button>
            </MDBox>
          </div>
        </div>
      </Modal>

      {isOpenModalLeaveInfo && (
        <ModalLeaveInfo
          isOpen={isOpenModalLeaveInfo}
          onClose={() => setIsOpenModalLeaveInfo(false)}
        />
      )}
    </>
  )
}

export default ModalLeaveApplication
